/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, {useState, useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {Flex} from '@instructure/ui-flex'
import {Heading} from '@instructure/ui-heading'
import {Text} from '@instructure/ui-text'
import {View} from '@instructure/ui-view'
import {Tabs} from '@instructure/ui-tabs'
import {Spinner} from '@instructure/ui-spinner'
import {Button} from '@instructure/ui-buttons'
import {IconDownloadLine} from '@instructure/ui-icons'
import {useScope as createI18nScope} from '@canvas/i18n'
import numberFormat from '@canvas/i18n/numberFormat'
import {buildSubmissionDownloadUrl} from '@canvas/assignments/react/FileSubmissionPreview'
import ErrorShip from '@instructure/platform-images/assets/ErrorShip.svg'
import {GenericErrorPage} from '@instructure/platform-generic-error-page'
import {reportError, canvasErrorPageTranslations} from '@canvas/error-page-utils'
import FriendlyDatetime from '@canvas/datetime/react/components/FriendlyDatetime'
import AssignmentDescription from '@canvas/assignments/react/AssignmentDescription'
import NeedsSubmissionPeerReview from '@canvas/assignments/react/NeedsSubmissionPeerReview'
import {useAssignmentQuery} from '../hooks/useAssignmentQuery'
import {useAllocatePeerReviews} from '../hooks/useAllocatePeerReviews'
import {useReviewerSubmissionQuery} from '../hooks/useReviewerSubmissionQuery'
import {PeerReviewSelector} from './PeerReviewSelector'
import AssignmentSubmission from './AssignmentSubmission'
import {WithBreakpoints, type Breakpoints} from '@instructure/platform-with-breakpoints'
import {isPeerReviewLocked, isPeerReviewPastLockDate} from '../utils/peerReviewLockUtils'
import LockedPeerReview from './LockedPeerReview'
import UnavailablePeerReview from './UnavailablePeerReview'
import PeerReviewPromptModal from '@canvas/assignments/react/PeerReviewPromptModal'
import {COMPLETED_PEER_REVIEW_TEXT} from '@canvas/assignments/helpers/PeerReviewHelpers'

const I18n = createI18nScope('peer_reviews_student')

export interface PeerReviewsStudentViewProps {
  assignmentId: string
  breakpoints: Breakpoints
}

const PeerReviewsStudentView: React.FC<PeerReviewsStudentViewProps> = ({
  assignmentId,
  breakpoints,
}) => {
  const [selectedTab, setSelectedTab] = useState<'details' | 'submission'>('details')
  const [selectedAssessmentIndex, setSelectedAssessmentIndex] = useState(0)
  const [hasCalledAllocate, setHasCalledAllocate] = useState(false)
  const [isWaitingForAllocationRefetch, setIsWaitingForAllocationRefetch] = useState(false)
  const [shouldNavigateToNext, setShouldNavigateToNext] = useState(false)
  const [peerReviewModalOpen, setPeerReviewModalOpen] = useState(false)
  const [hasSeenPeerReviewModal, setHasSeenPeerReviewModal] = useState(false)

  const userId = ENV.current_user_id || ''

  const queryClient = useQueryClient()
  const {data, isLoading, isError, isFetching} = useAssignmentQuery(assignmentId, userId)
  const {mutate: allocatePeerReviews, isPending: isAllocating} = useAllocatePeerReviews()
  const {data: reviewerSubmission} = useReviewerSubmissionQuery(assignmentId, userId || '')
  const isMobile = breakpoints.mobileOnly

  useEffect(() => {
    if (data?.assignment && !hasCalledAllocate) {
      const assignment = data.assignment
      const isLocked = isPeerReviewLocked(assignment)
      const isPastLockDate = isPeerReviewPastLockDate(assignment)

      // Don't allocate if the peer review is locked or past the lock date
      if (isLocked || isPastLockDate) {
        return
      }

      const assessmentRequestsCount = assignment.assessmentRequestsForCurrentUser?.length || 0
      const peerReviewsRequired = assignment.peerReviews?.count || 0
      const submissionRequired = assignment.peerReviews?.submissionRequired ?? false
      const hasSubmitted =
        assignment.submissionsConnection?.nodes &&
        assignment.submissionsConnection.nodes.length > 0 &&
        assignment.submissionsConnection.nodes[0]?.submittedAt
      const showSubmissionRequiredView = submissionRequired && !hasSubmitted

      if (!showSubmissionRequiredView && assessmentRequestsCount < peerReviewsRequired) {
        setHasCalledAllocate(true)
        setIsWaitingForAllocationRefetch(true)
        allocatePeerReviews({
          courseId: assignment.courseId,
          assignmentId: assignment._id,
        })
      }
    }
  }, [data, allocatePeerReviews, hasCalledAllocate])

  useEffect(() => {
    if (isWaitingForAllocationRefetch && !isFetching && data?.assignment) {
      setIsWaitingForAllocationRefetch(false)
    }
  }, [isWaitingForAllocationRefetch, isFetching, data])

  useEffect(() => {
    const assessmentRequests = data?.assignment?.assessmentRequestsForCurrentUser
    if (shouldNavigateToNext && assessmentRequests) {
      const currentReview = assessmentRequests[selectedAssessmentIndex]

      const nextAssignedReview = assessmentRequests.find(
        assessment =>
          assessment.workflowState === 'assigned' &&
          assessment.available === true &&
          assessment._id !== currentReview?._id,
      )

      if (nextAssignedReview) {
        const nextIndex = assessmentRequests.indexOf(nextAssignedReview)
        setSelectedAssessmentIndex(nextIndex)
      } else {
        const requiredCount = data?.assignment?.peerReviews?.count || 0
        const allocatedCount = assessmentRequests.length

        if (allocatedCount >= requiredCount) {
          setPeerReviewModalOpen(true)
          setHasSeenPeerReviewModal(true)
        } else {
          const availableCount = assessmentRequests.filter(a => a.available).length
          setSelectedAssessmentIndex(availableCount)
        }
      }

      setShouldNavigateToNext(false)
    }
  }, [shouldNavigateToNext, data, selectedAssessmentIndex])

  const handleNextPeerReview = () => {
    setShouldNavigateToNext(true)
  }

  const handlePeerReviewSubmitted = () => {
    queryClient.invalidateQueries({queryKey: ['peerReviewAssignment', assignmentId]})
  }

  const isUnavailableReviewSelected = () => {
    const availableCount = assessmentRequestsForCurrentUser?.filter(a => a.available)?.length || 0
    return selectedAssessmentIndex >= availableCount
  }

  if (isLoading || isAllocating || isWaitingForAllocationRefetch) {
    return (
      <View as="div" padding="medium" textAlign="center">
        <Spinner
          renderTitle={
            isAllocating ? I18n.t('Allocating peer reviews') : I18n.t('Loading assignment details')
          }
          size="large"
        />
      </View>
    )
  }

  if (isError || !data?.assignment) {
    return (
      <GenericErrorPage
        imageUrl={ErrorShip}
        onReportError={reportError}
        translations={canvasErrorPageTranslations}
        errorSubject={I18n.t('Student Peer Review Assignment error')}
        errorCategory={I18n.t('Student Peer Review Assignment Error Page.')}
        errorMessage={I18n.t('Failed to load assignment details.')}
      />
    )
  }

  const {
    assessmentRequestsForCurrentUser,
    name,
    description,
    peerReviews,
    submissionsConnection,
    peerReviewSubAssignment,
  } = data.assignment

  const submissionRequired = peerReviews?.submissionRequired ?? false
  const hasSubmitted =
    submissionsConnection?.nodes &&
    submissionsConnection.nodes.length > 0 &&
    submissionsConnection.nodes[0]?.submittedAt
  const showSubmissionRequiredView = submissionRequired && !hasSubmitted
  const isLocked = isPeerReviewLocked(data.assignment)
  const isPastLockDate = isPeerReviewPastLockDate(data.assignment)
  const peerReviewDueAt = peerReviewSubAssignment?.dueAt
  const isAnonymous = data.assignment.peerReviews?.anonymousReviews ?? false
  const selectedAssessment = assessmentRequestsForCurrentUser?.[selectedAssessmentIndex]
  const submissionUserId =
    selectedAssessment?.anonymizedUser?._id || selectedAssessment?.anonymousId || undefined

  const getUnavailableReason = () => {
    if (selectedAssessment && !selectedAssessment.submission?.submittedAt) {
      return I18n.t('This student has not yet submitted their work.')
    }
  }

  const renderPointsPossible = () =>
    !ENV.restrict_quantitative_data &&
    peerReviews?.pointsPossible != null && (
      <Flex.Item>
        <Text size={isMobile ? 'large' : 'x-large'} data-testid="total-points">
          {I18n.t(
            {one: '1 Point Possible', other: '%{formattedPoints} Points Possible'},
            {
              count: peerReviews.pointsPossible,
              formattedPoints: numberFormat._format(peerReviews.pointsPossible, {
                precision: 2,
                strip_insignificant_zeros: true,
              }),
            },
          )}
        </Text>
      </Flex.Item>
    )

  const renderHeader = () => {
    const pointsPossible = renderPointsPossible()

    return (
      <View
        as="div"
        borderWidth="0 0 small 0"
        borderColor="primary"
        padding="0 0 small 0"
        margin="0 0 medium 0"
      >
        <Flex gap="xSmall" direction="column">
          <Flex.Item>
            <Flex justifyItems="space-between" alignItems="start" gap="small">
              <Flex.Item shouldGrow shouldShrink>
                <Heading level="h1" aria-labelledby="peer-review-heading">
                  <Text
                    id="peer-review-heading"
                    size="x-large"
                    wrap="break-word"
                    data-testid="title"
                    weight={isMobile ? 'normal' : 'light'}
                  >
                    {I18n.t('%{name} Peer Review', {name: name})}
                  </Text>
                </Heading>
              </Flex.Item>
              {!isMobile && pointsPossible}
            </Flex>
          </Flex.Item>
          {peerReviewDueAt && (
            <Flex.Item>
              <Text size="medium" weight="bold">
                <FriendlyDatetime
                  data-testid="due-date"
                  prefix={I18n.t('Due:')}
                  prefixMobile={I18n.t('Due:')}
                  format={I18n.t('#date.formats.full_with_weekday')}
                  dateTime={peerReviewDueAt}
                  alwaysUseSpecifiedFormat
                />
              </Text>
            </Flex.Item>
          )}
          {isMobile && pointsPossible}
        </Flex>
      </View>
    )
  }

  const renderBody = () => {
    if (showSubmissionRequiredView) {
      return (
        <View as="div" margin="xx-large 0 0">
          <NeedsSubmissionPeerReview />
        </View>
      )
    }

    if (isLocked) {
      return <LockedPeerReview assignment={data.assignment} />
    }

    const hasAssessmentRequests =
      assessmentRequestsForCurrentUser && assessmentRequestsForCurrentUser.length > 0
    // Only hide Submission tab if past lock date AND no peer reviews were assigned (since no more allocations will happen)
    const showSubmissionTab = !isPastLockDate || hasAssessmentRequests

    return (
      <Tabs
        margin="x-small 0"
        onRequestTabChange={(_event, {index}) => {
          setSelectedTab(index === 0 ? 'details' : 'submission')
        }}
      >
        <Tabs.Panel
          id="assignment-details"
          renderTitle={isMobile ? I18n.t('Assignment') : I18n.t('Assignment Details')}
          isSelected={selectedTab === 'details'}
        >
          <View as="div" padding="medium 0">
            <AssignmentDescription description={description ?? undefined} />
          </View>
        </Tabs.Panel>
        {showSubmissionTab && (
          <Tabs.Panel
            id="submission"
            renderTitle={isMobile ? I18n.t('Peer Review') : I18n.t('Submission')}
            isSelected={selectedTab === 'submission'}
            padding="0"
            data-testid="submission-tab"
          >
            <div
              onFocus={(e: React.FocusEvent<HTMLDivElement>) => {
                const panel = e.currentTarget
                const relatedTarget = e.relatedTarget as HTMLElement | null
                if (relatedTarget && panel.contains(relatedTarget)) return
                panel.scrollIntoView({block: 'start', behavior: 'smooth'})
              }}
            >
              {isUnavailableReviewSelected() || !selectedAssessment?.submission?.submittedAt ? (
                <UnavailablePeerReview reason={getUnavailableReason()} />
              ) : (
                <AssignmentSubmission
                  submission={selectedAssessment.submission!}
                  isPeerReviewCompleted={selectedAssessment.workflowState === 'completed'}
                  rubricAssessment={selectedAssessment.rubricAssessment}
                  assignment={data.assignment}
                  reviewerSubmission={reviewerSubmission}
                  isMobile={isMobile}
                  handleNextPeerReview={handleNextPeerReview}
                  onPeerReviewSubmitted={handlePeerReviewSubmitted}
                  hasSeenPeerReviewModal={hasSeenPeerReviewModal}
                  isReadOnly={isPastLockDate}
                  isAnonymous={isAnonymous}
                  submissionUserId={submissionUserId}
                />
              )}
            </div>
          </Tabs.Panel>
        )}
      </Tabs>
    )
  }

  return (
    <>
      <View as="div">
        {renderHeader()}
        {isPastLockDate && <LockedPeerReview assignment={data.assignment} isPastLockDate={true} />}
        {data.assignment && !showSubmissionRequiredView && !isLocked && (
          <View as="div">
            <Flex gap="small" alignItems="start">
              <Flex.Item>
                <PeerReviewSelector
                  key={`${assessmentRequestsForCurrentUser?.length || 0}-peer-reviews`}
                  assessmentRequests={assessmentRequestsForCurrentUser || []}
                  selectedIndex={selectedAssessmentIndex}
                  onSelectionChange={setSelectedAssessmentIndex}
                  requiredPeerReviewCount={peerReviews?.count || 0}
                />
              </Flex.Item>
              {submissionUserId &&
                selectedAssessment?.submission?.submissionType === 'online_upload' &&
                selectedAssessment.submission?.attachments &&
                selectedAssessment.submission.attachments.length === 1 && (
                  <Flex.Item>
                    <Button
                      renderIcon={<IconDownloadLine />}
                      href={buildSubmissionDownloadUrl(
                        data.assignment.courseId,
                        data.assignment._id,
                        submissionUserId,
                        selectedAssessment.submission.attachments[0]._id,
                        data.assignment.peerReviews?.anonymousReviews ?? false,
                      )}
                    >
                      {I18n.t('Download Submission')}
                    </Button>
                  </Flex.Item>
                )}
            </Flex>
            {!isAnonymous && selectedAssessment?.anonymizedUser && (
              <View as="div" margin="x-small 0 0 xx-small">
                <Text size="medium" weight="bold">
                  {I18n.t('Peer: %{peerName}', {
                    peerName: selectedAssessment.anonymizedUser.displayName,
                  })}
                </Text>
              </View>
            )}
          </View>
        )}
        {renderBody()}
        <PeerReviewPromptModal
          headerText={[COMPLETED_PEER_REVIEW_TEXT]}
          headerMargin={'small 0 x-large'}
          peerReviewButtonText={null}
          open={peerReviewModalOpen}
          onClose={() => setPeerReviewModalOpen(false)}
          onRedirect={() => {}}
        />
      </View>
    </>
  )
}

export default WithBreakpoints(PeerReviewsStudentView)
